import { type ReactNode, useMemo } from "react";

import { ApolloProvider, gql, useQuery } from "@apollo/client";

// eslint-disable-next-line import/no-internal-modules
import { graphqlClient } from "site_manager_new/pages/SignatureProfile/api/client";

import { SignatureProfileEvents } from "./SignatureProfileEvents";
import { SignatureProfileExperienceCta } from "./SignatureProfileExperienceCta";
import { SignatureProfileFaq } from "./SignatureProfileFaq";
import { SignatureProfileGallery } from "./SignatureProfileGallery";
import { SignatureProfileHeader } from "./SignatureProfileHeader";
import { SignatureProfileHero } from "./SignatureProfileHero";
import { SignatureProfileTabs } from "./SignatureProfileTabs";
import { WithProviders } from "../utils";

const GET_PUBLIC_SIGNATURE_PROFILES = gql`
  query PublicSignatureProfiles(
    $id: ID
    $query: String
    $artistFirstName: String
    $artistLastName: String
    $styleTags: String
    $limit: Int
    $offset: Int
  ) {
    signatureProfiles(
      id: $id
      query: $query
      artistFirstName: $artistFirstName
      artistLastName: $artistLastName
      styleTags: $styleTags
      limit: $limit
      offset: $offset
    ) {
      id
      artistFirstName
      artistLastName
      artistPictureLargeUrl
      artistPictureSmallUrl
      styleTags
      location
      activeSince
      about
      leadContent
      biography
      artistStatement
      experienceLeadContent
      followArtistLink
      visitWebsiteLink
      contactLink
      publicLink
      logoUrl
      appHostUrl
      blogHomeUrl
      accountUrl
      products {
        id
        name
        pictureLargeUrl
        pictureSmallUrl
        categoryName
        url
      }
      exhibitionHistories {
        id
        title
        body
        year
      }
      publications {
        id
        title
        body
        pictureLargeUrl
        pictureSmallUrl
        publicatedAt
      }
      reviews {
        id
        name
        body
        rank
        moderatedByWebsiteOwner
      }
      instagrams {
        id
        visitLink
        profileTag
        posts
      }
      facebooks {
        id
        visitLink
        profileTag
        posts
      }
      faqs {
        id
        question
        answer
        moderatedByWebsiteOwner
      }
      upcomingEvents {
        id
        title
        location
        startedAt
        endedAt
        link
        pictureLargeUrl
        pictureSmallUrl
      }
      experienceLinks {
        id
        title
        link
      }
    }
  }
`;

const Html = ({ profile }: { profile: any }) => (
  <div className="min-h-screen bg-artist-offwhite">
    <SignatureProfileHeader
      data={{
        logoSrc: profile?.logoUrl,
        brandName: "Art Storefronts",
        nav: [
          { label: "Home", href: profile?.appHostUrl },
          { label: "About", href: profile?.blogHomeUrl },
        ],
        breadcrumbs: [
          { label: "Home", href: profile?.appHostUrl },
          {
            label:
              [profile?.artistFirstName, profile?.artistLastName]
                .filter(Boolean)
                .join(" ") || "Artist",
          },
        ],
        accountUrl: profile?.accountUrl,
      }}
    />
    <main>
      <SignatureProfileHero profile={profile} />
      <SignatureProfileGallery profile={profile} />
      <SignatureProfileTabs profile={profile} />
      {/* TODO SIGNATURE PROFILE INSTAGRAM https://artstorefronts.atlassian.net/browse/ART-8156 */}
      {/* <SignatureProfileInstagram profile={profile} /> */}
      {/* TODO SIGNATURE PROFILE FACEBOOK https://artstorefronts.atlassian.net/browse/ART-8155 */}
      {/* <SignatureProfileFacebook profile={profile} /> */}
      <SignatureProfileFaq profile={profile} />
      <SignatureProfileEvents profile={profile} />
      <SignatureProfileExperienceCta profile={profile} />
    </main>
    {/* TODO SIGNATURE PROFILE FOOTER https://artstorefronts.atlassian.net/browse/ART-8154 */}
    {/* <SignatureProfileFooter
      data={{
        logoSrc: "/lovable-uploads/dc41a512-5595-4af5-a2c5-341965a53abe.png",
        brandName: "artstorefronts",
        social: [
          {
            icon: "instagram",
            href: profile?.instagrams?.[0]?.visitLink || "#",
          },
          { icon: "twitter", href: "#" },
          { icon: "facebook", href: profile?.facebooks?.[0]?.visitLink || "#" },
          { icon: "youtube", href: "#" },
        ],
        columns: [
          {
            title: "Explore",
            links: [
              { label: "Artists", href: "#" },
              { label: "Galleries", href: "#" },
              { label: "Exhibitions", href: "#" },
              { label: "Art Advisory", href: "#" },
              { label: "Collections", href: "#" },
              { label: "Gift Cards", href: "#" },
            ],
          },
          {
            title: "Company",
            links: [
              { label: "About Us", href: "#" },
              { label: "Careers", href: "#" },
              { label: "Press", href: "#" },
              { label: "Blog", href: "#" },
              { label: "Partners", href: "#" },
              { label: "Contact", href: "#" },
            ],
          },
        ],
        contact: {
          address: ["123 Gallery Street", "New York, NY 10001"],
          phone: "+1 (212) 555-7890",
          email: "cs@artstorefronts.com",
        },
        bottomLinks: [
          { label: "Terms", href: "#" },
          { label: "Privacy", href: "#" },
          { label: "Cookies", href: "#" },
        ],
        copyright: "© 2023 Artify. All rights reserved.",
      }}
    /> */}
  </div>
);

const Inner: React.FC<{ id?: string | number }> = ({ id }) => {
  const { data, loading, error } = useQuery<{ signatureProfiles: any[] }>(
    GET_PUBLIC_SIGNATURE_PROFILES,
    {
      variables: {
        id,
        limit: 1,
        offset: 0,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const profile = useMemo(() => {
    const profiles = data?.signatureProfiles || [];

    return profiles[0];
  }, [data?.signatureProfiles]);

  if (loading && !profile) {
    return (
      <div className="min-h-screen bg-artist-offwhite flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-playfair text-artist-charcoal mb-4">
            Loading artist...
          </div>
          <div className="text-gray-500">
            Please wait while we fetch the profile
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load profile.
      </div>
    );
  }
  if (!profile) {
    return <div className="p-8 text-center">No public profiles found.</div>;
  }

  return <Html profile={profile} />;
};

const Component = ({
  children,
  id,
}: {
  children?: ReactNode;
  id?: string | number;
}) => (
  <WithProviders>
    <ApolloProvider client={graphqlClient}>
      <Inner {...(id === undefined ? {} : { id })} />
      {children}
    </ApolloProvider>
  </WithProviders>
);

export default Component;
